import { useEffect, useMemo, useState } from 'react'
import { FolderItem } from '../../types'
import { fileWithPathGuard } from '../../utils/guards'
import 'react-toastify/dist/ReactToastify.css'
import { useDropzone, FileWithPath } from 'react-dropzone'

import UploadingSkeleton from './UploadingSkeleton'
import UploadDropzone from './UploadDropzone'
import UploadFilesSection from './UploadFilesSection'
import UploadFooter from './UploadFooter'
import { useFilesState } from '../../context/FilesContext'

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column' as 'column',
  alignItems: 'center',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
}

const focusedStyle = {
  borderColor: '#2196f3',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

type Props = {
  folderStack?: FolderItem[]
}

export default function UploadContainer({ folderStack }: Props) {
  const { dispatch, refetch } = useFilesState()
  const [loading, setLoading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
    },
    onDragOver: () => setDragOver(true),
    onDragLeave: () => setDragOver(false),
    onDrop: () => setDragOver(false),
  })
  const [files, setFiles] = useState<FileWithPath[]>([])
  const [folderSet, setFolderSet] = useState<string[]>([])
  useEffect(() => {
    const set = new Set<string>()
    const filteredFiles = acceptedFiles.filter(fileWithPathGuard)
    filteredFiles.forEach((file) => {
      const path = file.path?.split('/')
      if (path && path.length > 1) set.add(path[1])
    })
    setFiles(filteredFiles)
    setFolderSet([...set])
  }, [acceptedFiles])

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  )

  const setUpload = (upload: boolean) => {
    dispatch({ type: 'SET_UPLOAD', payload: upload })
  }

  const setUploading = (loading: boolean) =>
    dispatch({ type: loading ? 'SET_UPLOADING' : 'SET_IDLE' })

  return (
    <div className="h-full min-w-[300px]">
      {loading && <UploadingSkeleton />}
      {!loading && (
        <>
          <div className="flex flex-col p-4 pb-0 h-[calc(100vh_-_70px)]">
            <UploadDropzone
              dragOver={dragOver}
              getInputProps={getInputProps}
              getRootProps={getRootProps}
              style={style}
            />
            {files.length > 0 && (
              <UploadFilesSection
                files={files}
                folders={folderSet}
                acceptedFiles={acceptedFiles}
                setFiles={setFiles}
                setFolderSet={setFolderSet}
              />
            )}
          </div>
        </>
      )}
      {!loading && files.length > 0 && (
        <UploadFooter
          acceptedFiles={acceptedFiles}
          files={files}
          refetchFiles={refetch}
          setFiles={setFiles}
          setFolderSet={setFolderSet}
          setLoading={setLoading}
          setUpload={setUpload}
          folderStack={folderStack}
          setUploading={setUploading}
        />
      )}
    </div>
  )
}
